import _ from "lodash";
import { createSelector } from "reselect";
import { Map } from "immutable";

import {
    canPermissionSelectorFactory,
    levelPermissionSelectorFactory,
} from "@auth/selectors";
import { getInitialState } from "../reducers";
import {
    Playlist,
    IPlaylist,
    PlaylistSnapshot,
    IPlaylistSnapshot,
    Track,
    ITrack,
    PlaylistTrack,
    IPlaylistTrack,
    PlaylistsMap,
    VendorState as State,
    VendorNormalisedEntities,
    getVendorName,
} from "../models";

export interface PropsWithId {
    params: {
        id: number;
    };
}

export const getEntities = (state: State) =>
    state.vendor.entities ? state.vendor.entities : getInitialState().entities;

export const getPlaylistFilters = (state: State) =>
    state.vendor.playlistFilters ? state.vendor.playlistFilters : getInitialState().playlistFilters;

export const getPlaylistTracksList = (state: State) =>
    state.vendor.playlistTracksList ? state.vendor.playlistTracksList : [];

const getIdFromProps = (_state: State, { params: { id } }: PropsWithId) => Number(id);

export const getPlaylists = createSelector(
    getEntities,
    (entities: VendorNormalisedEntities) => entities.playlists,
);

export const getTracks = createSelector(
    getEntities,
    (entities: VendorNormalisedEntities) => entities.vendorTracks,
);

export const getPlaylistTracks = createSelector(
    getEntities,
    (entities: VendorNormalisedEntities) => entities.playlistTracks,
);

// Get individual entities
const getPlaylist = ({ vendor: { entities } }: State, props: PropsWithId) => {
    const id = getIdFromProps(null, props);
    if (entities && entities.playlists.has(id)) {
        const playlist = entities.playlists.get(id);
        if (playlist.exists) {
            return playlist;
        }
        else {
            return null;
        }
    }
    return new Playlist({ id });
};

export const makeGetPlaylist = () => createSelector(
    getPlaylist,
    getEntities,
    (
        item: Playlist,
        entities: VendorNormalisedEntities,
    ) => {
        const hydrate = makeHydratePlaylist(entities);
        return hydrate(item) as Playlist;
    },
);

// Get map of playlists
export const getPlaylistsMap = createSelector(
    getPlaylists,
    (items: Map<number, Playlist>) => {
        const map: PlaylistsMap = {
            "spotify": {
                vendorKey: "spotify",
                vendorName: getVendorName("spotify"),
                playlists: [],
            },
            "deezer": {
                vendorKey: "deezer",
                vendorName: getVendorName("deezer"),
                playlists: [],
            },
            "apple-music": {
                vendorKey: "apple-music",
                vendorName: getVendorName("apple-music"),
                playlists: [],
            },
            "napster": {
                vendorKey: "napster",
                vendorName: getVendorName("napster"),
                playlists: [],
            },
        };

        items.forEach((playlist) => {
            if (map.hasOwnProperty(playlist.vendor)) {
                map[playlist.vendor].playlists.push(playlist);
            }
        });

        return _.pickBy(map, (item) => {
            return item.playlists.length > 0;
        });
    },
);

// Get array of entities
export const getPlaylistTracksListEntities = createSelector(
    getPlaylistTracksList,
    getPlaylistTracks,
    getEntities,
    (
        list: string[],
        items: Map<string, PlaylistTrack>,
        entities: VendorNormalisedEntities,
    ) => {
        const hydrate = makeHydratePlaylistTrack(entities);
        // Only return existing hydrated deliveries.
        return _.reduce(list, (result, id) => {
                if (items.has(id)) {
                    result.push(items.get(id));
                }
                return result;
            }, [] as PlaylistTrack[])
            .map((item) => hydrate(item) as PlaylistTrack);
    },
);

// Hydrate
export const makeHydratePlaylist = (entities: VendorNormalisedEntities) =>
    (e: Playlist | number, depth: number = 0): Playlist => {
        // if it's an ID, get it from the state.
        if (typeof e === "number") {
            e = entities.playlists.get(e);
        }

        if (e == null) {
            // Nothing to hydrate
            return e as Playlist;
        }

        const playlist = {} as IPlaylist;
        // The snapshots
        const hydrateSnapshot = makeHydratePlaylistSnapshot(entities);
        if (e.snapshots != null && e.snapshots.length > 0) {
            const snapshots = new Array(e.snapshots.length);
            for (let i = 0; i < e.snapshots.length; i++) {
                snapshots[i] = hydrateSnapshot(e.snapshots[i], depth) as PlaylistSnapshot;
            }
            e = e.setCast("snapshots", snapshots);
        }
        else {
            e = e.setCast("snapshots", undefined);
        }

        // And the computed data
        e = e.setCast("viewUrl", e.getViewUrl());
        e = e.setCast("viewText", e.getViewText());
        e = e.setCast("hydrated", true);
        e = e.setCast("vendorName", e.getVendorName());
        e = e.setCast("imageUrl", e.getMediumImage());

        return e.merge(playlist) as Playlist;
    };

export const makeHydratePlaylistSnapshot = (entities: VendorNormalisedEntities) =>
    (e: PlaylistSnapshot | number, depth: number = 0): PlaylistSnapshot => {
        // if it's an ID, get it from the state.
        if (typeof e === "number") {
            e = entities.playlistSnapshots.get(e);
        }

        if (e == null) {
            // Nothing to hydrate
            return e as PlaylistSnapshot;
        }

        const snapshot = {} as IPlaylistSnapshot;
        // The tracks
        const hydrateTrack = makeHydrateTrack(entities);

        if (e.tracks != null && e.tracks.length > 0) {
            const tracks = new Array(e.tracks.length);
            for (let i = 0; i < e.tracks.length; i++) {
                tracks[i] = hydrateTrack(e.tracks[i], e.id, depth) as Track;
            }
            e = e.set("tracks", tracks) as PlaylistSnapshot;
        }
        else {
            e = e.set("tracks", undefined) as PlaylistSnapshot;
        }
        // Set the number of owned tracks
        e = e.set("numOwnedTracks", _.filter(
            e.tracks,
            (t: Track) => t.trackId != null,
        ).length) as PlaylistSnapshot;

        return e.merge(snapshot) as PlaylistSnapshot;
    };

export const makeHydrateTrack = (entities: VendorNormalisedEntities) =>
    (e: Track | number, snapshotId?: number, depth: number = 0): Track => {
        // if it's an ID, get it from the state.
        if (typeof e === "number") {
            e = entities.vendorTracks.get(e);
        }

        if (e == null) {
            // Nothing to hydrate
            return e as Track;
        }

        const track = {} as ITrack;
        // The pivot
        let pivotKey: string = null;
        if (snapshotId != null) {
            pivotKey = snapshotId + "_" + e.id;
        }
        if (pivotKey != null && entities.playlistSnapshotTracks.has(pivotKey)) {
            track.pivot = entities.playlistSnapshotTracks.get(pivotKey);
        }
        else {
            track.pivot = undefined;
        }
        // Hydrate the playlists
        const hydratePlaylist = makeHydratePlaylist(entities);
        if (e.playlists != null && e.playlists.length > 0 && depth < 1) {
            const playlists = new Array(e.playlists.length);
            for (let i = 0; i < e.playlists.length; i++) {
                playlists[i] = hydratePlaylist(e.playlists[i], ++depth) as Playlist;
            }
            e = e.setCast("playlists", playlists);
        }
        else {
            e = e.setCast("playlists", undefined);
        }
        e = e.setCast("vendorName", e.getVendorName());

        return e.merge(track) as Track;
    };

export const makeHydratePlaylistTrack = (entities: VendorNormalisedEntities) =>
    (e: PlaylistTrack | string): PlaylistTrack => {
        // if it's an ID, get it from the state.
        if (typeof e === "string") {
            e = entities.playlistTracks.get(e);
        }

        if (e == null) {
            // Nothing to hydrate
            return e as PlaylistTrack;
        }

        const playlistTrack = {} as IPlaylistTrack;
        // Hydrate the playlist track
        const hydrateTrack = makeHydrateTrack(entities);
        const hydratePlaylist = makeHydratePlaylist(entities);
        // The track
        if (e.trackId != null) {
            playlistTrack.track = hydrateTrack(e.trackId);
        }
        // The playlist
        if (e.playlistId != null) {
            playlistTrack.playlist = hydratePlaylist(e.playlistId);
        }

        return e.merge(playlistTrack) as PlaylistTrack;
    };

// Permissions selectors
const playlistPermissionPrefix = "wata.playlists.playlist";
export const canViewPlaylist = canPermissionSelectorFactory(playlistPermissionPrefix, "view");
export const getViewPlaylistLevel = levelPermissionSelectorFactory(playlistPermissionPrefix, "view");
export const canTrackPlaylist = canPermissionSelectorFactory(playlistPermissionPrefix, "track");
export const getTrackPlaylistLevel = levelPermissionSelectorFactory(playlistPermissionPrefix, "track");
